import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, Plus, Trash2, Save, Send, UserPlus, Heading, Package, Sparkles, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const units = [
  { value: "u", label: "Unité" },
  { value: "h", label: "Heure" },
  { value: "j", label: "Jour" },
  { value: "m", label: "Mètre" },
  { value: "m2", label: "m\u00B2" },
  { value: "kg", label: "kg" },
  { value: "forf", label: "Forfait" },
];

export default function DevisFormPage() {
  const { apiFetch, API } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [contactId, setContactId] = useState("");
  const [objet, setObjet] = useState("");
  const [items, setItems] = useState([{ description: "", quantity: 0, unit: "u", unit_price: 0, item_type: "title" }, { description: "", quantity: 1, unit: "u", unit_price: 0, item_type: "product" }]);
  const [taxRate, setTaxRate] = useState(0);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [showPaymentTerms, setShowPaymentTerms] = useState(true);
  const [devisDate, setDevisDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [devisData, setDevisData] = useState(null);
  const [ccRecipients, setCcRecipients] = useState([]);
  const [hideCc, setHideCc] = useState(false);
  const [showCcPrompt, setShowCcPrompt] = useState(false);

  // Inline client creation
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ company_name: "", contact_name: "", email: "", phone: "", address: "", city: "", postal_code: "", siret: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [correctingIdx, setCorrectingIdx] = useState(null);

  const correctDescription = async (idx) => {
    const text = items[idx]?.description;
    if (!text?.trim()) return;
    setCorrectingIdx(idx);
    try {
      const res = await apiFetch(`${API}/devis/correct-text`, { method: "POST", body: JSON.stringify({ text }) });
      if (res.ok) {
        const { corrected } = await res.json();
        if (corrected !== text) {
          updateItem(idx, "description", corrected);
          toast.success("Texte corrigé");
        } else {
          toast.info("Aucune correction nécessaire");
        }
      } else {
        toast.error("Erreur de correction");
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setCorrectingIdx(null); }
  };

  useEffect(() => {
    const fetchData = async () => {
      const res = await apiFetch(`${API}/clients`);
      if (res.ok) setClients(await res.json());

      if (isEdit) {
        const dRes = await apiFetch(`${API}/devis/${id}`);
        if (dRes.ok) {
          const d = await dRes.json();
          setDevisData(d);
          setClientId(d.client_id);
          setContactId(d.contact_id || "");
          setObjet(d.objet || "");
          setItems(d.items.map(i => ({
            description: i.description,
            quantity: i.quantity,
            unit: i.unit,
            unit_price: i.unit_price,
            item_type: i.item_type || "product"
          })));
          setTaxRate(d.tax_rate);
          setValidUntil(d.valid_until || "");
          setNotes(d.notes || "");
          setShowPaymentTerms(d.show_payment_terms !== false);
          setDevisDate(d.created_at ? d.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10));
          setCcRecipients(d.cc_recipients || []);
          setHideCc(d.hide_cc || false);
        }
      }
    };
    fetchData();
  }, [id, isEdit, apiFetch, API]);

  const addItem = (type = "product") => {
    const newItem = { description: "", quantity: type === "forfait" ? 0 : 1, unit: "u", unit_price: 0, item_type: type };
    if (type === "title") {
      const lastTitleIdx = items.map((it, i) => it.item_type === "title" ? i : -1).filter(i => i !== -1);
      if (lastTitleIdx.length === 0) {
        setItems([newItem, ...items]);
      } else {
        const insertAfter = lastTitleIdx[lastTitleIdx.length - 1];
        const updated = [...items];
        updated.splice(insertAfter + 1, 0, newItem);
        setItems(updated);
      }
    } else {
      setItems([...items, newItem]);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const subtotal = items.reduce((sum, item) => (item.item_type === "title" || item.item_type === "forfait") ? sum : sum + (item.quantity * item.unit_price), 0);
  const taxAmount = subtotal * taxRate / 100;
  const total = subtotal + taxAmount;

  const handleCreateClient = async () => {
    if (!newClient.company_name || !newClient.contact_name || !newClient.email) {
      toast.error("Nom société, contact et email sont requis");
      return;
    }
    setCreatingClient(true);
    try {
      const res = await apiFetch(`${API}/clients`, { method: "POST", body: JSON.stringify(newClient) });
      if (res.ok) {
        const created = await res.json();
        setClients(prev => [created, ...prev]);
        setClientId(created.client_id);
        setShowNewClient(false);
        setNewClient({ company_name: "", contact_name: "", email: "", phone: "", address: "", city: "", postal_code: "", siret: "" });
        toast.success("Client créé et sélectionné");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur de création"); }
    finally { setCreatingClient(false); }
  };

  const handleSave = async () => {
    if (!clientId) { toast.error("Veuillez sélectionner un client"); return; }
    if (items.some(i => !i.description)) { toast.error("Veuillez remplir toutes les descriptions"); return; }

    setSaving(true);
    const payload = { client_id: clientId, contact_id: contactId, objet, items, tax_rate: taxRate, valid_until: validUntil || null, notes, show_payment_terms: showPaymentTerms, devis_date: devisDate, cc_recipients: ccRecipients.filter(cc => cc.email), hide_cc: hideCc };

    try {
      let res;
      if (isEdit) {
        res = await apiFetch(`${API}/devis/${id}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        res = await apiFetch(`${API}/devis`, { method: "POST", body: JSON.stringify(payload) });
      }

      if (res.ok) {
        toast.success(isEdit ? "Devis mis à jour" : "Devis créé");
        navigate("/devis");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
      }
    } finally { setSaving(false); }
  };

  const handleSend = async () => {
    if (!isEdit || !devisData) return;
    // If no CC recipients, prompt user first
    const validCc = ccRecipients.filter(cc => cc.email);
    if (validCc.length === 0 && !showCcPrompt) {
      setShowCcPrompt(true);
      return;
    }
    setShowCcPrompt(false);
    setSending(true);
    try {
      const res = await apiFetch(`${API}/devis/${id}/send`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Devis envoyé par email");
        navigate("/devis");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } finally { setSending(false); }
  };

  const handleSendWithoutCc = async () => {
    setShowCcPrompt(false);
    setSending(true);
    try {
      const res = await apiFetch(`${API}/devis/${id}/send`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Devis envoyé par email");
        navigate("/devis");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'envoi");
      }
    } finally { setSending(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-5xl" data-testid="devis-form-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/devis")} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="font-barlow text-xl sm:text-2xl font-bold text-slate-900 tracking-tight uppercase">
            {isEdit ? `Modifier ${devisData?.devis_number || ""}` : "Nouveau Devis"}
          </h1>
          {isEdit && devisData?.status && devisData.status !== "draft" && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              devisData.status === "sent" ? "bg-blue-50 text-blue-700" :
              devisData.status === "envoye_simple" ? "bg-violet-50 text-violet-700" :
              devisData.status === "accepted" ? "bg-emerald-50 text-emerald-700" :
              devisData.status === "rejected" ? "bg-red-50 text-red-700" :
              "bg-slate-100 text-slate-600"
            }`} data-testid="devis-status-badge">
              {devisData.status === "sent" ? "Envoyé" : devisData.status === "envoye_simple" ? "Envoyé (PDF)" : devisData.status === "accepted" ? "Accepté" : devisData.status === "rejected" ? "Refusé" : devisData.status === "annule" ? "Annulé" : devisData.status}
              <span className="ml-1 text-[10px] opacity-70">- le statut ne changera pas</span>
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Object */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Client & Objet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-700">Client</Label>
                {isEdit && devisData && devisData.status !== "draft" ? (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-9 px-3 py-2 bg-slate-100 border border-slate-200 rounded-md text-sm text-slate-700 flex items-center" data-testid="client-locked">
                      {clients.find(c => c.client_id === clientId)?.company_name || devisData.client_name || "Client"}
                      <span className="ml-auto text-[10px] text-slate-400">verrouillé</span>
                    </div>
                  </div>
                ) : (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={clientId} onValueChange={v => { setClientId(v); setContactId(""); }}>
                      <SelectTrigger data-testid="client-select-trigger">
                        <SelectValue placeholder="Sélectionner un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id} data-testid={`client-option-${c.client_id}`}>
                            {c.company_name} - {c.contact_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setShowNewClient(true)} title="Créer un nouveau client" data-testid="new-client-inline-btn">
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
                )}
              </div>
              {/* Contact selector - shows when client has multiple contacts */}
              {clientId && (() => {
                const selectedClient = clients.find(c => c.client_id === clientId);
                const contacts = selectedClient?.contacts || [];
                if (contacts.length <= 1) return null;
                return (
                  <div>
                    <Label className="text-slate-700">Destinataire</Label>
                    <Select value={contactId} onValueChange={setContactId}>
                      <SelectTrigger data-testid="contact-select-trigger">
                        <SelectValue placeholder="Choisir le contact" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map(ct => (
                          <SelectItem key={ct.contact_id} value={ct.contact_id} data-testid={`contact-option-${ct.contact_id}`}>
                            {ct.name} {ct.position ? `(${ct.position})` : ""} — {ct.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
              <div>
                <Label className="text-slate-700">Objet du devis</Label>
                <Input
                  placeholder="Ex: Travaux de soudure sur site industriel"
                  value={objet}
                  onChange={e => setObjet(e.target.value)}
                  data-testid="objet-input"
                />
              </div>

              {/* CC Recipients */}
              <div className="space-y-2.5 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700">Copie (CCi)</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="add-cc-btn">
                        <Plus className="w-3 h-3" /> Ajouter CCi
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto w-80">
                      <DropdownMenuItem onClick={() => setCcRecipients(prev => [...prev, { email: "", name: "", can_accept: false }])} className="text-slate-500 italic text-xs" data-testid="cc-manual-entry">
                        <Plus className="w-3.5 h-3.5 mr-2" /> Saisie manuelle
                      </DropdownMenuItem>
                      <Separator className="my-1" />
                      {clients.map(c => {
                        const allContacts = [
                          { email: c.email, name: c.contact_name || c.company_name, label: `${c.company_name} — ${c.contact_name || c.email}` },
                          ...(c.contacts || []).map(ct => ({ email: ct.email, name: ct.name, label: `${c.company_name} — ${ct.name}${ct.position ? ` (${ct.position})` : ""}` }))
                        ];
                        return allContacts.map((ct, i) => {
                          const alreadyAdded = ccRecipients.some(cc => cc.email === ct.email);
                          return (
                            <DropdownMenuItem
                              key={`${c.client_id}-${i}`}
                              disabled={alreadyAdded}
                              onClick={() => setCcRecipients(prev => [...prev, { email: ct.email, name: ct.name, can_accept: false }])}
                              className={`text-xs ${alreadyAdded ? "opacity-40" : ""}`}
                            >
                              <span className="truncate">{ct.label} — {ct.email}</span>
                            </DropdownMenuItem>
                          );
                        });
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {ccRecipients.length > 0 && (
                  <div className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-md">
                    <p className="text-[11px] text-slate-600">Masquer les CCi aux destinataires</p>
                    <Switch checked={hideCc} onCheckedChange={setHideCc} data-testid="hide-cc-toggle" />
                  </div>
                )}
                {ccRecipients.map((cc, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row gap-2 p-2.5 bg-blue-50/50 border border-blue-100 rounded-md" data-testid={`cc-row-${idx}`}>
                    <Input placeholder="Email" type="email" value={cc.email} onChange={e => { const u = [...ccRecipients]; u[idx] = { ...u[idx], email: e.target.value }; setCcRecipients(u); }} className="h-8 text-sm flex-1" data-testid={`cc-email-${idx}`} />
                    <Input placeholder="Nom" value={cc.name} onChange={e => { const u = [...ccRecipients]; u[idx] = { ...u[idx], name: e.target.value }; setCcRecipients(u); }} className="h-8 text-sm flex-1" data-testid={`cc-name-${idx}`} />
                    <div className="flex items-center gap-2 shrink-0">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={cc.can_accept} onChange={e => { const u = [...ccRecipients]; u[idx] = { ...u[idx], can_accept: e.target.checked }; setCcRecipients(u); }} className="rounded border-slate-300 h-3.5 w-3.5" data-testid={`cc-can-accept-${idx}`} />
                        <span className="text-[10px] text-slate-600 whitespace-nowrap">Peut accepter</span>
                      </label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setCcRecipients(prev => prev.filter((_, i) => i !== idx))} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" data-testid={`cc-remove-${idx}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Lignes du devis</CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => addItem("title")} className="gap-1 text-xs" data-testid="add-title-btn">
                  <Heading className="w-3.5 h-3.5" /> Titre
                </Button>
                <Button variant="outline" size="sm" onClick={() => addItem("forfait")} className="gap-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="add-forfait-btn">
                  <Package className="w-3.5 h-3.5" /> Forfait
                </Button>
                <Button variant="outline" size="sm" onClick={() => addItem("product")} className="gap-1 text-xs" data-testid="add-item-btn">
                  <Plus className="w-3.5 h-3.5" /> Produit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Desktop header */}
              <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 uppercase px-1">
                <div className="col-span-1"></div>
                <div className="col-span-1">Type</div>
                <div className="col-span-3">Description</div>
                <div className="col-span-2">Quantité</div>
                <div className="col-span-1">Unité</div>
                <div className="col-span-2">Prix Unit.</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1"></div>
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="devis-items">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                      {items.map((item, idx) => (
                        <Draggable key={`item-${idx}`} draggableId={`item-${idx}`} index={idx}>
                          {(dragProvided, snapshot) => (
                            <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} style={dragProvided.draggableProps.style} className={snapshot.isDragging ? "opacity-90 shadow-lg rounded-md" : ""}>
                              {item.item_type === "title" ? (
                                /* Title row */
                                <div className="flex gap-2 items-center bg-slate-800 rounded-md p-2" data-testid={`item-row-${idx}`}>
                                  <div {...dragProvided.dragHandleProps} className="shrink-0 cursor-grab active:cursor-grabbing" data-testid={`item-drag-${idx}`}>
                                    <GripVertical className="w-4 h-4 text-slate-400" />
                                  </div>
                                  <div className="shrink-0">
                                    <Heading className="w-4 h-4 text-white" />
                                  </div>
                                  <Input
                                    placeholder="Titre de section (ex: SERRURERIE)"
                                    value={item.description}
                                    onChange={e => updateItem(idx, "description", e.target.value)}
                                    className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 font-bold uppercase text-sm"
                                    data-testid={`item-desc-${idx}`}
                                  />
                                  <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-400" data-testid={`item-remove-${idx}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : item.item_type === "forfait" ? (
                                /* Forfait row - description only, no price */
                                <div data-testid={`item-row-${idx}`}>
                                  {/* Mobile */}
                                  <div className="sm:hidden space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-1.5 text-xs text-amber-600">
                                        <div {...dragProvided.dragHandleProps} className="cursor-grab active:cursor-grabbing"><GripVertical className="w-3.5 h-3.5 text-amber-400" /></div>
                                        <Package className="w-3 h-3" /> Forfait (sans prix)
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => correctDescription(idx)} disabled={correctingIdx === idx} className="h-7 w-7 p-0 text-blue-400 hover:text-blue-600" title="Correction IA">
                                          {correctingIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500">
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                    <Textarea placeholder="Description du forfait" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} className="text-sm min-h-[80px]" rows={2} />
                                  </div>
                                  {/* Desktop */}
                                  <div className="hidden sm:grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-1">
                                      <div {...dragProvided.dragHandleProps} className="flex items-center justify-center h-10 cursor-grab active:cursor-grabbing" data-testid={`item-drag-${idx}`}>
                                        <GripVertical className="w-4 h-4 text-slate-300 hover:text-slate-500" />
                                      </div>
                                    </div>
                                    <div className="col-span-1">
                                      <div className="flex items-center justify-center h-10 text-xs text-amber-500">
                                        <Package className="w-3.5 h-3.5" />
                                      </div>
                                    </div>
                                    <div className="col-span-8">
                                      <Textarea placeholder="Description du forfait (sans prix)" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} className="min-h-[80px] text-sm resize-y border-amber-200 bg-amber-50/50" rows={2} data-testid={`item-desc-${idx}`} />
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end gap-0.5">
                                      <Button variant="ghost" size="sm" onClick={() => correctDescription(idx)} disabled={correctingIdx === idx} className="h-8 w-8 p-0 text-blue-400 hover:text-blue-600" title="Correction IA" data-testid={`item-ai-${idx}`}>
                                        {correctingIdx === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" data-testid={`item-remove-${idx}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* Product row */
                                <div data-testid={`item-row-${idx}`}>
                                  {/* Mobile layout */}
                                  <div className="sm:hidden space-y-2 p-3 bg-slate-50 rounded-md">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                        <div {...dragProvided.dragHandleProps} className="cursor-grab active:cursor-grabbing"><GripVertical className="w-3.5 h-3.5 text-slate-400" /></div>
                                        <Package className="w-3 h-3" /> Produit
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => correctDescription(idx)} disabled={correctingIdx === idx} className="h-7 w-7 p-0 text-blue-400 hover:text-blue-600" title="Correction IA" data-testid={`item-ai-mobile-${idx}`}>
                                          {correctingIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" data-testid={`item-remove-mobile-${idx}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                    <Textarea placeholder="Description (sauts de ligne possibles)" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} className="text-sm min-h-[100px]" rows={3} />
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label className="text-[10px] text-slate-400">Qté</Label>
                                        <Input type="number" min="0" step="0.5" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="text-sm" />
                                      </div>
                                      <div>
                                        <Label className="text-[10px] text-slate-400">Unité</Label>
                                        <Select value={item.unit} onValueChange={v => updateItem(idx, "unit", v)}>
                                          <SelectTrigger className="h-10 text-xs"><SelectValue /></SelectTrigger>
                                          <SelectContent>{units.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                      </div>
                                      <div>
                                        <Label className="text-[10px] text-slate-400">P.U.</Label>
                                        <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="text-sm" />
                                      </div>
                                    </div>
                                    <div className="text-right text-sm font-semibold text-slate-700">
                                      = {(item.quantity * item.unit_price).toFixed(2)} EUR
                                    </div>
                                  </div>
                                  {/* Desktop layout */}
                                  <div className="hidden sm:grid grid-cols-12 gap-2 items-start">
                                    <div className="col-span-1">
                                      <div {...dragProvided.dragHandleProps} className="flex items-center justify-center h-10 cursor-grab active:cursor-grabbing" data-testid={`item-drag-${idx}`}>
                                        <GripVertical className="w-4 h-4 text-slate-300 hover:text-slate-500" />
                                      </div>
                                    </div>
                                    <div className="col-span-1">
                                      <div className="flex items-center justify-center h-10 text-xs text-slate-400">
                                        <Package className="w-3.5 h-3.5" />
                                      </div>
                                    </div>
                                    <div className="col-span-3">
                                      <Textarea placeholder="Description du service (sauts de ligne possibles)" value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} className="min-h-[100px] text-sm resize-y" rows={3} data-testid={`item-desc-${idx}`} />
                                    </div>
                                    <div className="col-span-2">
                                      <Input type="number" min="0" step="0.5" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} data-testid={`item-qty-${idx}`} />
                                    </div>
                                    <div className="col-span-1">
                                      <Select value={item.unit} onValueChange={v => updateItem(idx, "unit", v)}>
                                        <SelectTrigger className="h-10 text-xs" data-testid={`item-unit-${idx}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>{units.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-2">
                                      <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} data-testid={`item-price-${idx}`} />
                                    </div>
                                    <div className="col-span-1 text-right text-sm font-medium text-slate-900 pt-2.5">
                                      {(item.quantity * item.unit_price).toFixed(2)}
                                    </div>
                                    <div className="col-span-1 flex items-center justify-end gap-0.5">
                                      <Button variant="ghost" size="sm" onClick={() => correctDescription(idx)} disabled={correctingIdx === idx} className="h-8 w-8 p-0 text-blue-400 hover:text-blue-600" title="Correction IA" data-testid={`item-ai-${idx}`}>
                                        {correctingIdx === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" data-testid={`item-remove-${idx}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </CardContent>
          </Card>

          {/* Options */}
          <Card className="border-slate-200">
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Taux de taxe (%)</Label>
                  <Input type="number" min="0" max="100" step="0.1" value={taxRate} onChange={e => setTaxRate(parseFloat(e.target.value) || 0)} data-testid="tax-rate-input" />
                  <p className="text-[11px] text-slate-400">0% par défaut (Guyane française)</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Valable jusqu'au</Label>
                  <Input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} data-testid="valid-until-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Date du devis</Label>
                  <Input type="date" value={devisDate} onChange={e => setDevisDate(e.target.value)} data-testid="devis-date-input" />
                </div>
              </div>

              {/* Payment terms toggle */}
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="payment-terms-section">
                <div>
                  <p className="text-sm font-medium text-amber-900">Conditions de paiement</p>
                  <p className="text-[11px] text-amber-700">Acompte 50% à la validation du devis</p>
                </div>
                <Switch
                  checked={showPaymentTerms}
                  onCheckedChange={setShowPaymentTerms}
                  data-testid="payment-terms-toggle"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-700">Notes / Conditions</Label>
                <Textarea placeholder="Notes ou conditions particulières..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} data-testid="notes-input" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar recap */}
        <div className="space-y-6">
          <Card className="border-slate-200 sticky top-8">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Récapitulatif</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Sous-total</span>
                <span className="font-medium">{subtotal.toFixed(2)} EUR</span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Taxe ({taxRate}%)</span>
                  <span className="font-medium">{taxAmount.toFixed(2)} EUR</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="font-semibold text-slate-900">Net à payer</span>
                <span className="text-xl font-bold text-slate-900 font-barlow">{total.toFixed(2)} EUR</span>
              </div>

              {/* Item type summary */}
              <div className="text-xs text-slate-400 space-y-0.5">
                <p>{items.filter(i => i.item_type === "product").length} produit(s) · {items.filter(i => i.item_type === "title").length} titre(s) · {items.filter(i => i.item_type === "forfait").length} forfait(s)</p>
                {showPaymentTerms && <p className="text-amber-600">Conditions de paiement activées</p>}
                {!showPaymentTerms && <p className="text-slate-400">Conditions de paiement désactivées</p>}
              </div>

              <Separator />
              <div className="space-y-2 pt-2">
                <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleSave} disabled={saving} data-testid="save-devis-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
                {isEdit && devisData && (
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSend} disabled={sending} data-testid="send-devis-btn">
                    <Send className="w-4 h-4 mr-2" /> {sending ? "Envoi..." : "Envoyer par email"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Client Dialog */}
      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-barlow text-lg uppercase tracking-tight flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Nouveau Client
            </DialogTitle>
            <DialogDescription>Créez un client directement depuis le devis</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Société *</Label>
                <Input value={newClient.company_name} onChange={e => setNewClient(p => ({ ...p, company_name: e.target.value }))} placeholder="Nom de la société" data-testid="new-client-company" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Contact *</Label>
                <Input value={newClient.contact_name} onChange={e => setNewClient(p => ({ ...p, contact_name: e.target.value }))} placeholder="Nom du contact" data-testid="new-client-contact" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} placeholder="email@exemple.com" data-testid="new-client-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Téléphone</Label>
                <Input value={newClient.phone} onChange={e => setNewClient(p => ({ ...p, phone: e.target.value }))} placeholder="0694..." data-testid="new-client-phone" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Adresse</Label>
              <Input value={newClient.address} onChange={e => setNewClient(p => ({ ...p, address: e.target.value }))} placeholder="Adresse" data-testid="new-client-address" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Code postal</Label>
                <Input value={newClient.postal_code} onChange={e => setNewClient(p => ({ ...p, postal_code: e.target.value }))} placeholder="97300" data-testid="new-client-postal" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ville</Label>
                <Input value={newClient.city} onChange={e => setNewClient(p => ({ ...p, city: e.target.value }))} placeholder="Cayenne" data-testid="new-client-city" />
              </div>
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <Label className="text-xs">SIRET</Label>
                <Input value={newClient.siret} onChange={e => setNewClient(p => ({ ...p, siret: e.target.value }))} placeholder="SIRET" data-testid="new-client-siret" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewClient(false)}>Annuler</Button>
              <Button onClick={handleCreateClient} disabled={creatingClient} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="create-client-confirm-btn">
                <UserPlus className="w-4 h-4" /> {creatingClient ? "Création..." : "Créer et sélectionner"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CC Prompt Dialog - shown when sending without CCi */}
      <Dialog open={showCcPrompt} onOpenChange={setShowCcPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow text-base uppercase tracking-tight">Options d'envoi</DialogTitle>
            <DialogDescription>
              Comment souhaitez-vous envoyer ce devis ?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button onClick={handleSendWithoutCc} disabled={sending} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="send-without-cc-btn">
              <p className="text-sm font-medium text-slate-800">{sending ? "Envoi..." : "Envoyer avec portail de validation"}</p>
              <p className="text-xs text-slate-400 mt-0.5">Le client pourra accepter/refuser en ligne</p>
            </button>
            <button onClick={async () => { setShowCcPrompt(false); setSending(true); try { const res = await apiFetch(`${API}/devis/${id}/send-pdf`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }); if (res.ok) { const d = await res.json(); toast.success(d.message); navigate("/devis"); } else { const err = await res.json(); toast.error(err.detail || "Erreur"); } } finally { setSending(false); } }} disabled={sending} className="w-full text-left p-3 rounded-lg border border-violet-200 hover:bg-violet-50 transition-colors" data-testid="send-pdf-btn">
              <p className="text-sm font-medium text-violet-700">{sending ? "Envoi..." : "Envoyer en PDF simple (sans validation)"}</p>
              <p className="text-xs text-slate-400 mt-0.5">PDF en pièce jointe, vous validez manuellement après</p>
            </button>
            <button onClick={() => { setShowCcPrompt(false); setCcRecipients(prev => [...prev, { email: "", name: "", can_accept: false }]); }} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:bg-amber-50 transition-colors" data-testid="add-cc-from-prompt-btn">
              <p className="text-sm font-medium text-slate-800">Ajouter des CCi d'abord</p>
              <p className="text-xs text-slate-400 mt-0.5">Ajouter des destinataires en copie avant d'envoyer</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
